/**
 * Takes the Poseidon session token off an `/internal/open` URL before anything else in the app
 * runs. The app entry imports this module first: Firebase Analytics sends a page view carrying
 * `document.location` as soon as the client module loads, and the router snapshots the location
 * when it mounts, so the fragment has to be gone before either happens. Clearing it here also
 * keeps it out of the tab's session history and any referrer.
 */

export const INTERNAL_OPEN_PATH = "/internal/open";

/** The `token` key of a URL fragment (`#token=…`), or null when absent or empty. */
export function readTokenFromHash(hash: string): string | null {
  const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
  const token = params.get("token");
  return token && token.length > 0 ? token : null;
}

let captured: string | null = null;

/** The token captured at load, handed out once so a remount cannot sign in with it again. */
export function takeInternalToken(): string | null {
  const token = captured;
  captured = null;
  return token;
}

/**
 * On `/internal/open`, keeps the fragment's token for `takeInternalToken` and drops the fragment
 * from the URL, preserving the history state and query. Runs when this module is imported.
 */
export function captureInternalToken(): void {
  if (window.location.pathname !== INTERNAL_OPEN_PATH) return;
  captured = readTokenFromHash(window.location.hash);
  const { pathname, search } = window.location;
  window.history.replaceState(window.history.state, "", pathname + search);
}

captureInternalToken();
